import React, { useState, useEffect, useCallback, CSSProperties } from "react";
import { api } from "../../services/api";
import { useAuth } from "../../global-components/src";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { Helmet } from "react-helmet-async";

// ---------------------------------------------------------------------------
// Config da API
// ---------------------------------------------------------------------------
// Ajuste esses caminhos para bater com as rotas reais do seu api.php.
// Os nomes abaixo foram deduzidos a partir do ElectionController que você
// mandou (current / vote / results / myVote).
const ROUTES = {
  current: `/elections/current`,
  vote: `/elections/vote`,
  results: `/elections/results`,
  myVote: `/elections/my-vote`,
};

// Se você usa Laravel Sanctum em modo SPA (cookies de sessão), é preciso
// pegar o cookie de CSRF antes de fazer o POST do voto. Se sua auth for por
// token (Bearer), pode remover essa função e o "await getCsrfCookie()" nela
// referenciado mais abaixo.
async function getCsrfCookie(): Promise<void> {
  try {
    await fetch("/sanctum/csrf-cookie", { credentials: "include" });
  } catch {
    // se não usar sanctum, ignora
  }
}

function getXsrfTokenFromCookie(): string | null {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const xsrf = getXsrfTokenFromCookie();
  return fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(xsrf ? { "X-XSRF-TOKEN": xsrf } : {}),
      ...(options.headers || {}),
    },
    ...options,
  });
}

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
interface Foto {
  url: string;
  legenda: string;
}

interface Candidato {
  id: number;
  numero: string;
  nome: string;
  partido: string;
  foto: Foto;
}

interface Etapa {
  id: number;
  titulo: string;
  numeros: number;
  candidatos: Candidato[];
}

interface Voto {
  etapa: string;
  voto: string; // número do candidato ou "branco"
}

// Formatos que vêm do back-end (ElectionController)
interface ApiCandidate {
  id: number;
  number: number | string;
  name: string;
  party: string;
  photo?: string | null;
  active?: boolean;
}

interface ApiPosition {
  id: number;
  name: string;
  digits: number;
  order?: number;
  candidates: ApiCandidate[];
}

interface ApiElection {
  id: number;
  title: string;
  active: boolean;
  positions: ApiPosition[];
}

interface ApiMyVoteResponse {
  voted: boolean;
  votes: Array<{
    id: number;
    position_id: number;
    candidate_id: number | null;
    is_blank: boolean;
    is_null: boolean;
    candidate?: ApiCandidate | null;
  }>;
}

interface ApiResultCandidate {
  id: number;
  number: number | string;
  name: string;
  party: string;
  photo?: string | null;
  votes: number;
  percent: number;
}

interface ApiResultPosition {
  id: number;
  name: string;
  digits: number;
  total_votes: number;
  blank_votes: number;
  null_votes: number;
  candidates: ApiResultCandidate[];
}

interface ApiResultsResponse {
  election: string;
  positions: ApiResultPosition[];
}

type Fase = "carregando" | "votando" | "ja-votou" | "erro" | "sem-eleicao";

// ---------------------------------------------------------------------------
// Helpers de mapeamento API -> modelo local (mantendo o mesmo shape usado
// pela UI original)
// ---------------------------------------------------------------------------
function mapPositionToEtapa(position: ApiPosition): Etapa {
  return {
    id: position.id,
    titulo: position.name,
    numeros: position.digits,
    candidatos: position.candidates
      .filter((c) => c.active !== false)
      .map((c) => {
        const numero = String(c.number).padStart(position.digits, "0");
        return {
          id: c.id,
          numero,
          nome: c.name,
          partido: c.party,
          foto: {
            url: c.photo || "",
            legenda: c.name,
          },
        };
      }),
  };
}

const CHART_COLORS = ["#48C079", "#F09046", "#4A90D9", "#C0487F", "#B8A248", "#7C48C0"];

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
export default function UrnaEletronica(): React.JSX.Element {
  const [fase, setFase] = useState<Fase>("carregando");
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [resultados, setResultados] = useState<ApiResultsResponse | null>(null);
  const { token } = useAuth();
  const [etapaAtual, setEtapaAtual] = useState<number>(0);
  const [numero, setNumero] = useState<string>("");
  const [votoBranco, setVotoBranco] = useState<boolean>(false);
  const [votos, setVotos] = useState<Voto[]>([]);
  const [fim, setFim] = useState<boolean>(false);
  const [enviando, setEnviando] = useState<boolean>(false);
  const [erroVoto, setErroVoto] = useState<string | null>(null);

  const etapa: Etapa | undefined = etapas[etapaAtual];

  const numeroCompleto: boolean = !!etapa && numero.length === etapa.numeros;
  const mostrarResultado: boolean = votoBranco || numeroCompleto;

  const candidatoEncontrado: Candidato | undefined =
    etapa && numeroCompleto
      ? etapa.candidatos.find((c) => c.numero === numero)
      : undefined;

  // -------------------------------------------------------------------
  // Carregamento inicial: verifica se já votou; se sim, busca resultados;
  // se não, busca a eleição ativa e monta as etapas.
  // -------------------------------------------------------------------
  const carregar = useCallback(async () => {
    setFase("carregando");
    try {
      await getCsrfCookie();

      const meuVotoResp = await api(token).get(ROUTES.myVote);
      const meuVoto: ApiMyVoteResponse = meuVotoResp.data;

      if (meuVoto.voted) {
        const resultadosResp = await api(token).get(ROUTES.results);
        const dadosResultados: ApiResultsResponse = resultadosResp.data;
        setResultados(dadosResultados);
        setFase("ja-votou");
        return;
      }

      const eleicaoResp = await api(token).get(ROUTES.current);
     

      const eleicao: ApiElection = await eleicaoResp.data;
      const positionsOrdenadas = [...eleicao.positions].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
      );
      setEtapas(positionsOrdenadas.map(mapPositionToEtapa));
      setEtapaAtual(0);
      setFase("votando");
    } catch (e: any) {
      if (e.status === 404) {
        setFase("sem-eleicao");
        return;
      }
      setFase("erro");
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // -------------------------------------------------------------------
  // Ações (equivalentes às funções do script.js)
  // -------------------------------------------------------------------
  function clicou(n: string): void {
    if (fim || enviando) return;
    if (votoBranco) return; // já escolheu branco, teclado numérico não faz nada
    if (!etapa || numero.length >= etapa.numeros) return; // todos os dígitos já preenchidos

    setNumero((atual) => `${atual}${n}`);
  }

  function branco(): void {
    if (fim || enviando) return;
    if (numero === "") {
      setVotoBranco(true);
    }
  }

  function corrige(): void {
    if (fim || enviando) return;
    setNumero("");
    setVotoBranco(false);
    setErroVoto(null);
  }

  async function confirma(): Promise<void> {
    if (fim || enviando || !etapa) return;

    const podeConfirmar = votoBranco || numero.length === etapa.numeros;
    if (!podeConfirmar) return;

    setEnviando(true);
    setErroVoto(null);

    try {
      const resp = await api(token).post(ROUTES.vote, {
        position_id: etapa.id,
        candidate_number: votoBranco ? null : Number(numero),
        blank: votoBranco,
      });
      
      const dados = await resp.data;

      // if (!resp.ok) {
      //   setErroVoto(dados?.message || "Erro ao registrar voto.");
      //   setEnviando(false);
      //   return;
      // }

      const novoVoto: Voto = {
        etapa: etapa.titulo,
        voto: votoBranco ? "branco" : numero,
      };

      const novosVotos = [...votos, novoVoto];
      setVotos(novosVotos);

      const proximaEtapa = etapaAtual + 1;
      if (etapas[proximaEtapa] !== undefined) {
        setEtapaAtual(proximaEtapa);
        setNumero("");
        setVotoBranco(false);
      } else {
        setFim(true);
        carregar();
      }
    } catch (e: any) {
      // setErroVoto("Erro de conexão ao registrar voto.");
      setErroVoto(e?.message || "Erro ao registrar voto.");
      setEnviando(false);
      return;
    } finally {
      setEnviando(false);
    }
  }

  const Header = () => {
    return (
      <Helmet>
        <title>Paçoca - Eleições 2026</title>
        <meta name="description" content="Eleições 2026 na Paçoca" />
        <meta name="keywords" content="Paçoca, eleições, 2026" />
        <meta name="author" content="Paçoca Inc." />
        <meta property="og:title" content="Paçoca - Eleições 2026" />
        <meta property="og:description" content="Eleições 2026 na Paçoca" />
        <meta property="og:image" content="/img/link-preview.png?v=1" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Paçoca - Eleições 2026" />
        <meta name="twitter:description" content="Eleições 2026 na Paçoca" />
        <meta name="twitter:image" content="/img/link-preview.png?v=1" />
    </Helmet>
    )
  }

  // -------------------------------------------------------------------
  // Render: carregando / erro / sem eleição
  // -------------------------------------------------------------------
  if (fase === "carregando") {
    return (
      <div className="body" style={styles.body}>
        {Header()}
        <style>{cssResponsive}</style>
        <div className="urna" style={styles.urna}>
          <div className="tela" style={styles.tela}>
            <div style={styles.avisoGigante}>CARREGANDO...</div>
          </div>
        </div>
      </div>
    );
  }

  if (fase === "sem-eleicao") {
    return (
      <div className="body" style={styles.body}>
        {Header()}
        <style>{cssResponsive}</style>
        <div className="urna" style={styles.urna}>
          <div className="tela" style={styles.tela}>
            <div style={styles.avisoGigante}>NENHUMA ELEIÇÃO ATIVA</div>
          </div>
        </div>
      </div>
    );
  }

  if (fase === "erro") {
    return (
      <div className="body" style={styles.body}>
        {Header()}
        <style>{cssResponsive}</style>
        <div className="urna" style={styles.urna}>
          <div className="tela" style={styles.tela}>
            <div style={{ ...styles.avisoGigante, fontSize: 28 }}>
              ERRO AO CARREGAR
              <br />
              <span
                style={{ fontSize: 16, cursor: "pointer", textDecoration: "underline" }}
                onClick={carregar}
              >
                tentar novamente
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Render: já votou -> mostra gráfico dos resultados
  // -------------------------------------------------------------------
  if (fase === "ja-votou" && resultados) {
    return (
      <div className="body" style={styles.body}>
        {Header()}
        <style>{cssResponsive}</style>
        <div className="urna" style={styles.urna}>
          <div className="tela" style={{ ...styles.tela, padding: 16, overflowY: "auto" }}>
            <div style={{ color: "#000", fontWeight: "bold", marginBottom: 8 }}>
              VOCÊ JÁ VOTOU — RESULTADO: {resultados.election}
            </div>

            {resultados.positions.map((position) => {
              const chartData = [
                ...position.candidates.map((c) => ({
                  nome: `${c.number} - ${c.name}`,
                  votos: c.votes,
                  percent: c.percent,
                })),
                { nome: "Branco", votos: position.blank_votes, percent: 0 },
                { nome: "Nulo", votos: position.null_votes, percent: 0 },
              ];

              return (
                <div key={position.id} style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                    {position.name} — {position.total_votes} votos
                  </div>
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nome" angle={-30} textAnchor="end" interval={0} height={60} tick={{ fontSize: 10 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="votos">
                          <LabelList dataKey="votos" position="top" />
                          {chartData.map((_, idx) => (
                            <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Render: votando (fluxo original da urna)
  // -------------------------------------------------------------------
  if (!etapa) {
    return (
      <div className="body" style={styles.body}>
        {Header()}
        <style>{cssResponsive}</style>
        <div className="urna" style={styles.urna}>
          <div className="tela" style={styles.tela}>
            <div style={styles.avisoGigante}>NENHUM CARGO DISPONÍVEL</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="body" style={styles.body}>
      {Header()}
      <style>{cssResponsive}</style>

      <div className="urna" style={styles.urna}>
        <div className="tela" style={styles.tela}>
          {fim ? (
            <div style={styles.avisoGigante}>FIM</div>
          ) : (
            <>
              <div className="d1" style={styles.d1}>
                <div style={styles.d1Left}>
                  <div className="d11" style={styles.d11}>
                    {mostrarResultado && <span>SEU VOTO PARA O (A)</span>}
                  </div>
                  <div className="d12" style={styles.d12}>
                    <span>{etapa.titulo}</span>
                  </div>

                  <div className="d13" style={styles.d13}>
                    {!votoBranco &&
                      Array.from({ length: etapa.numeros }).map((_, i) => {
                        const preenchido = i < numero.length;
                        const piscando = i === numero.length;
                        return (
                          <div
                            key={i}
                            className="numero"
                            style={{
                              ...styles.numero,
                              ...(piscando
                                ? { animation: "pisca 1s infinite" }
                                : {}),
                            }}
                          >
                            {preenchido ? numero[i] : ""}
                          </div>
                        );
                      })}
                  </div>

                  <div className="d14" style={styles.d14}>
                    {mostrarResultado &&
                      (candidatoEncontrado ? (
                        <div>Nome: {candidatoEncontrado.nome}</div>
                      ) : (
                        <div className="avisoGrande" style={styles.avisoGrande}>
                          VOTO NULO
                        </div>
                      ))}
                    {votoBranco && (
                      <div className="avisoGrande" style={styles.avisoGrande}>
                        VOTO EM BRANCO
                      </div>
                    )}
                    {erroVoto && (
                      <div style={{ color: "#b00020", fontSize: 12, marginTop: 8 }}>
                        {erroVoto}
                      </div>
                    )}
                    {enviando && (
                      <div style={{ fontSize: 12, marginTop: 8 }}>Enviando voto...</div>
                    )}
                  </div>
                </div>

                <div className="d1Right" style={styles.d1Right}>
                  {mostrarResultado && candidatoEncontrado
                    ? candidatoEncontrado.foto && (
                        <div key={0} style={styles.d1Image}>
                          <div className="placeholderFoto" style={styles.placeholderFoto}>
                            {candidatoEncontrado.foto.legenda
                              .split(" ")
                              .map((p) => p[0])
                              .join("")}
                          </div>
                          <p style={{ margin: 0, color: "#fff" }}>
                            {candidatoEncontrado.foto.legenda}
                          </p>
                        </div>
                      )
                    : null}
                </div>
              </div>

              <div className="d2" style={styles.d2}>
                Aperte na tela: <br />
                CONFIRMA para CONFIRMAR este voto <br />
                CORRIGE para REINICIAR este voto
              </div>
            </>
          )}
        </div>

        <div className="teclado" style={styles.teclado}>
          <div style={styles.tecladoLinha}>
            {["1", "2", "3"].map((n) => (
              <div
                key={n}
                className="tecladoBotao"
                style={styles.tecladoBotao}
                onClick={() => clicou(n)}
              >
                {n}
              </div>
            ))}
          </div>
          <div style={styles.tecladoLinha}>
            {["4", "5", "6"].map((n) => (
              <div
                key={n}
                className="tecladoBotao"
                style={styles.tecladoBotao}
                onClick={() => clicou(n)}
              >
                {n}
              </div>
            ))}
          </div>
          <div style={styles.tecladoLinha}>
            {["7", "8", "9"].map((n) => (
              <div
                key={n}
                className="tecladoBotao"
                style={styles.tecladoBotao}
                onClick={() => clicou(n)}
              >
                {n}
              </div>
            ))}
          </div>
          <div style={styles.tecladoLinha}>
            <div
              className="tecladoBotao"
              style={styles.tecladoBotao}
              onClick={() => clicou("0")}
            >
              0
            </div>
          </div>
          <div style={styles.tecladoLinha1}>
            <div
              className="tecladoBotao botaoConfirma"
              style={{ ...styles.tecladoBotao, ...styles.botaoBranco }}
              onClick={branco}
            >
              BRANCO
            </div>
            <div
              className="tecladoBotao botaoConfirma"
              style={{ ...styles.tecladoBotao, ...styles.botaoCorrige }}
              onClick={corrige}
            >
              CORRIGE
            </div>
            <div
              className="tecladoBotao botaoConfirma"
              style={{ ...styles.tecladoBotao, ...styles.botaoConfirma }}
              onClick={confirma}
            >
              CONFIRMA
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estilos responsivos (via <style>, pois inline style não suporta media query)
// ---------------------------------------------------------------------------
// Estratégia: os tamanhos "base" continuam definidos no objeto `styles`
// (mesmos valores do layout original, usados como valores máximos em telas
// grandes). O bloco abaixo usa clamp()/vw para encolher tudo
// proporcionalmente em telas menores e, só numa quebra bem pequena, empilha
// tela + teclado — sem alterar a composição visual original.
const cssResponsive = `
@keyframes pisca {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
}
.body{
  padding: clamp(0px, 2vw, 16px) !important;
}
.urna {
  width: min(900px, 96vw);
  max-width: 100%;
  box-sizing: border-box;
}

.tela {
  width: 100% !important;
  height: clamp(260px, 46vw, 400px) !important;
  box-sizing: border-box;
}

.teclado {
  box-sizing: border-box;
  flex: 0 0 auto;
  padding: 20px
}

.tecladoBotao {
  font-size: clamp(0.95rem, 2.6vw, .8rem) !important;
  padding: clamp(0.4rem, 1vw, 0.5rem) clamp(0.8rem, 3vw, 1.5rem) clamp(0.15rem, 0.6vw, 0.25rem) clamp(0.3rem, 1vw, 0.5rem) !important;
  margin: clamp(0.25rem, 1vw, 0.45rem) !important;
  text-align: center;
}

.botaoConfirma {
  width: clamp(75px, 16vw, 90px) !important;
  font-size: clamp(0.75rem, 2.6vw, .8rem) !important;
}

.numero {
  width: clamp(26px, 8vw, 40px) !important;
  height: clamp(26px, 8vw, 40px) !important;
  font-size: clamp(1rem, 4vw, 1.5rem) !important;
}

.d11, .d12 {
  font-size: clamp(0.7rem, 2.2vw, 1rem);
}

.d13 {
  gap: clamp(3px, 1vw, 6px) !important;
}

.placeholderFoto {
  height: clamp(60px, 14vw, 90px) !important;
  font-size: clamp(16px, 4vw, 24px) !important;
}

.d1Right {
  width: clamp(90px, 26vw, 140px) !important;
}

.avisoGrande {
  font-size: clamp(1rem, 3.6vw, 1.5rem) !important;
}

.d2 {
  font-size: clamp(0.6rem, 1.8vw, 0.75rem) !important;
}

/* Em telas bem estreitas, empilha tela e teclado (o teclado ocupa a
   largura toda) mantendo a mesma ordem visual: tela em cima, teclado
   embaixo — o layout original já é assim em termos de hierarquia. */
@media (max-width: 480px) {
  .urna {
    flex-direction: column;
    align-items: stretch;
    padding: 16px !important;
  }
  .teclado {
    align-items: center;
  }
  .d1 {
    flex-direction: column;
  }
  .d1Right {
    width: 100% !important;
    flex-direction: row !important;
    align-items: center;
    gap: 8px;
  }
}
`;

const styles: Record<string, CSSProperties> = {
  body: {
    margin: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "Arial, Helvetica, sans-serif",
    minHeight: "100vh",
    padding: 16,
    boxSizing: "border-box",
  },
  urna: {
    backgroundColor: "#dbd5c9",
    padding: 30,
    display: "flex",
  },
  tela: {
    display: "flex",
    flexDirection: "column",
    width: "700px",
    height: "400px",
    background: "linear-gradient(to right, #e8e8e8, #c1c1c1)",
    borderStyle: "inset",
    borderWidth: "6px",
  },
  teclado: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "center",
    padding: "1rem 0",
    borderStyle: "outset",
    backgroundColor: "#403a3a",
  },
  tecladoLinha: {
    display: "flex",
    justifyContent: "center",
  },
  tecladoLinha1: {
    display: "flex",
    justifyContent: "center",
    marginTop: "0.5rem",
  },
  tecladoBotao: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0.45rem",
    padding: "0.5rem 1.5rem 0.25rem 0.5rem",
    fontSize: "1.5rem",
    backgroundColor: "black",
    border: "none",
    borderRadius: "4px",
    color: "whitesmoke",
    cursor: "pointer",
    boxShadow: "4px -4px 1px #222222",
  },
  botaoBranco: {
    backgroundColor: "#FFF",
    color: "#000",
    fontSize: "x-small",
    height: 40,
  },
  botaoCorrige: {
    backgroundColor: "#F09046",
    color: "#000",
    fontSize: "x-small",
    height: 40,
  },
  botaoConfirma: {
    backgroundColor: "#48C079",
    color: "#000",
    fontSize: "x-small",
    width: 90,
    height: 60,
    marginBottom: -50,
  },
  d1: {
    flexGrow: 1,
    display: "flex",
  },
  d1Left: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    paddingLeft: 8,
  },
  d11: {
    height: 28,
    display: "flex",
    alignItems: "center",
  },
  d12: {
    height: 28,
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
  },
  d13: {
    height: 48,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  d14: {
    flexGrow: 1,
    paddingTop: 8,
  },
  numero: {
    display: "inline-flex",
    width: 40,
    height: 40,
    border: "solid #000 thin",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "x-large",
    backgroundColor: "#fff",
  },
  d1Right: {
    width: 140,
    display: "flex",
    flexDirection: "column",
    padding: 4,
  },
  d1Image: {
    border: "solid #000 thin",
    backgroundColor: "#000",
    textAlign: "center",
    fontSize: "x-small",
    padding: 4,
    marginBottom: 4,
  },
  placeholderFoto: {
    width: "100%",
    height: 90,
    backgroundColor: "#333",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    fontWeight: "bold",
  },
  d2: {
    minHeight: 60,
    fontSize: "x-small",
    borderTop: "solid #000 4px",
    paddingLeft: 8,
    paddingTop: 4,
  },
  avisoGrande: {
    fontSize: "x-large",
    fontWeight: "bold",
  },
  avisoGigante: {
    fontSize: 48,
    fontWeight: "bold",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
};